import React, {Fragment, useContext, useEffect} from 'react';
import {DateUtility} from "../common/DateUtility";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {useRenderableData} from "./hooks/useRenderableData";
import {PillButton} from "./PillButton";

interface DateRangeFilterProps {
    filterByDateRange: Function;
    filterStartDateString: string;
    filterEndDateString: string;
}

export const DateRangeFilter = function (props: DateRangeFilterProps) {
    const noSelectionDisplay = 'Filter by invoice date';
    const renderableData = useRenderableData({
        showPicker: false,
        filterStartDateString: props.filterStartDateString,
        filterEndDateString: props.filterEndDateString,
        pickerSelectionDisplay: getSelectionDisplay(
            DateUtility.dateFilterStringToJsDate(props.filterStartDateString),
            DateUtility.dateFilterStringToJsDate(props.filterEndDateString)
        )
    });

    useEffect(() => {
        renderableData.updateAndRender({
            filterStartDateString: props.filterStartDateString,
            filterEndDateString: props.filterEndDateString,
            pickerSelectionDisplay: getSelectionDisplay(
                DateUtility.dateFilterStringToJsDate(props.filterStartDateString),
                DateUtility.dateFilterStringToJsDate(props.filterEndDateString)
            )
        })
    }, [props.filterStartDateString, props.filterEndDateString]);

    function getSelectionDisplay(filterStartDate: Date | null, filterEndDate: Date | null) {
        let selectionDisplay = noSelectionDisplay
        if (filterStartDate || filterEndDate) {
            if (filterStartDate && filterEndDate) {
                if (
                    DateUtility.jsDateToMoment(filterStartDate)
                        .isSame(DateUtility.jsDateToMoment(filterEndDate), 'day')
                ) {
                    selectionDisplay = 'On ' + DateUtility.jsDateToFilterDateDisplay(filterStartDate)
                } else {
                    selectionDisplay =
                        DateUtility.jsDateToFilterDateDisplay(filterStartDate) +
                        ' to ' +
                        DateUtility.jsDateToFilterDateDisplay(filterEndDate)
                }
            } else if (filterStartDate) {
                selectionDisplay = 'On or after ' + DateUtility.jsDateToFilterDateDisplay(filterStartDate)
            } else if (filterEndDate) {
                selectionDisplay = 'On or before ' + DateUtility.jsDateToFilterDateDisplay(filterEndDate)
            }
        }
        return selectionDisplay
    }

    function togglePicker() {
        renderableData.updateAndRender({showPicker: !renderableData.showPicker})
    }

    function handleDateChange(fieldName: string, date: Date | null) {
        let selection = DateUtility.jsDateToFilterString(date)
        let isStartDate = fieldName === 'filterStartDateString'
        let isEndDate = fieldName === 'filterEndDateString'
        let currentSelection
        if (isStartDate) {
            currentSelection = '' + renderableData.filterStartDateString
        } else if (isEndDate) {
            currentSelection = '' + renderableData.filterEndDateString
        }
        if (selection === currentSelection) {
            selection = ''
        }
        renderableData.updateAndRender({
            [fieldName]: selection,
            pickerSelectionDisplay: getSelectionDisplay(
                DateUtility.dateFilterStringToJsDate(isStartDate ? selection : renderableData.filterStartDateString),
                DateUtility.dateFilterStringToJsDate(isEndDate ? selection : renderableData.filterEndDateString)
            )
        });
    }

    function clearSelections() {
        renderableData.updateAndRender({
            filterStartDateString: '',
            filterEndDateString: '',
            pickerSelectionDisplay: getSelectionDisplay(null, null),
            showPicker: false,
        });
        window.setTimeout(function () {
            props.filterByDateRange('', '')
        }, 0)
    }

    function filterByDateRange() {
        togglePicker()
        props.filterByDateRange(renderableData.filterStartDateString, renderableData.filterEndDateString)
    }

    function closePickerFromUnderlay() {
        togglePicker()
        if (
            props.filterStartDateString !== renderableData.filterStartDateString ||
            props.filterEndDateString !== renderableData.filterEndDateString
        ) {
            props.filterByDateRange(renderableData.filterStartDateString, renderableData.filterEndDateString)
        }
    }

    function beforeEndDate(date: Date) {
        let filterEndDateMoment = DateUtility.dateFilterStringToMoment(renderableData.filterEndDateString);
        let passedDateMoment = DateUtility.jsDateToMoment(date);
        if (filterEndDateMoment && passedDateMoment) {
            return passedDateMoment.isSameOrBefore(filterEndDateMoment)
        } else {
            return true;
        }
    }

    function afterStartDate(date: Date) {
        let filterStartDateMoment = DateUtility.dateFilterStringToMoment(renderableData.filterStartDateString);
        let passedDateMoment = DateUtility.jsDateToMoment(date);
        if (filterStartDateMoment && passedDateMoment) {
            return passedDateMoment.isSameOrAfter(filterStartDateMoment)
        } else {
            return true;
        }
    }

    let noSelectionClass = renderableData.pickerSelectionDisplay === noSelectionDisplay ? 'no-selection' : ''
    let filterStartDate = DateUtility.dateFilterStringToJsDate(renderableData.filterStartDateString);
    let filterEndDate = DateUtility.dateFilterStringToJsDate(renderableData.filterEndDateString);

    return (
        <div className={`date-range-filter ${renderableData.showPicker ? 'open' : ''}`}>
            <div
                className={`date-range-filter-opener ${noSelectionClass}`}
                onClick={togglePicker}
            >
                {renderableData.pickerSelectionDisplay}
            </div>
            <div className="date-range-filter-box">
                <div className="box-triangle"></div>
                <div className="date-range-filter-pickers">
                    <div className="date-range-filter-picker-container">
                        <div className="date-range-filter-picker start-date-picker">
                            <div className="date-range-filter-picker-widget">
                                <div className="manual-entry-picker">
                                    <DatePicker
                                        selected={filterStartDate}
                                        onChange={(date) => handleDateChange('filterStartDateString', date)}
                                        filterDate={beforeEndDate}
                                        popperClassName="manual-entry-picker-box"
                                        placeholderText="Start Date (MM/DD/YYYY)"
                                    />
                                </div>
                                <DatePicker
                                    selected={filterStartDate}
                                    onSelect={(date) => handleDateChange('filterStartDateString', date)}
                                    inline
                                    disabledKeyboardNavigation
                                    filterDate={beforeEndDate}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="date-range-filter-picker-container">
                        <div className="date-range-filter-picker end-date-picker">
                            <div className="date-range-filter-picker-widget">
                                <div className="manual-entry-picker">
                                    <DatePicker
                                        selected={filterEndDate}
                                        onChange={(date) => handleDateChange('filterEndDateString', date)}
                                        filterDate={afterStartDate}
                                        popperClassName="manual-entry-picker-box"
                                        placeholderText="End Date (MM/DD/YYYY)"
                                    />
                                </div>
                                <DatePicker
                                    selected={filterEndDate}
                                    onSelect={(date) => handleDateChange('filterEndDateString', date)}
                                    inline
                                    disabledKeyboardNavigation
                                    filterDate={afterStartDate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="date-range-filter-button-section">
                    <div className="date-range-filter-buttons">
                        <PillButton
                            onClick={clearSelections}
                            containerClasses="date-range-filter-clear-button-container"
                            buttonClasses="date-range-filter-clear-button"
                            buttonText="CLEAR"
                        />
                        <PillButton
                            onClick={filterByDateRange}
                            containerClasses="date-range-filter-submit-button-container"
                            buttonClasses="date-range-filter-submit-button"
                            buttonText="APPLY FILTERS"
                        />
                    </div>
                </div>
            </div>
            <div
                className="date-range-filter-picker-underlay"
                onClick={closePickerFromUnderlay}
            ></div>
        </div>
    )
}
